import React from 'react';
import { useParams } from 'react-router-dom';
import { useGetSharedPublicPostQuery } from '@/api/client';
import { FullEventDetailScreen } from '@feature/event-detail/FullEventDetailScreen';
import { EventPost } from '@feature/event-detail/types';

export default function FullEventDetailPage(): React.ReactElement {
  const { eventId: shareId } = useParams<{ eventId: string }>();

  React.useEffect(() => {
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    return () => window.removeEventListener('resize', setVH);
  }, []);

  const {
    data: post,
    isLoading,
    error,
  } = useGetSharedPublicPostQuery(shareId || '', {
    skip: !shareId,
  });

  if (!shareId) {
    return (
      <div style={styles.errorContainer}>
        <h1>Invalid Event Link</h1>
        <p>The event link appears to be invalid.</p>
        <a href="/download">Download the Hereabout App</a>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}>Loading event...</div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={styles.errorContainer}>
        <h1>Event Not Found</h1>
        <p>This event may be private or no longer available.</p>
        <a href="/download">Download the Hereabout App</a>
      </div>
    );
  }

  if (post.post_type !== 'event') {
    return (
      <div style={styles.errorContainer}>
        <h1>Not an Event</h1>
        <p>This link does not point to an event.</p>
        <a href="/download">Download the Hereabout App</a>
      </div>
    );
  }

  return <FullEventDetailScreen event={post as EventPost} />;
}

const styles: Record<string, React.CSSProperties> = {
  errorContainer: {
    minHeight: 'calc(var(--vh, 1vh) * 100)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    textAlign: 'center',
  },
  loadingContainer: {
    minHeight: 'calc(var(--vh, 1vh) * 100)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingSpinner: {
    fontSize: '16px',
    color: '#666',
  },
};
